/**
 * What: Authenticated fetch helper for Shopify embedded app API calls.
 * Transport / data-flow: Uses App Bridge's intercepted fetch for ID-token injection and its single auth retry.
 *
 */
import { readShopifyApiError } from './shopifyApiError'
import { settleShopifyConnectionRecovery } from './shopifyQueryClient'
import { waitForShopifyOperation } from './shopifyRequestLifecycle'
import { observeShopifyStartupRequest } from './startupTiming'
import {
  SHOPIFY_ACCOUNT_PASS_HEADER,
  SHOPIFY_EXPECTED_SESSION_IDENTITY_HEADER,
  readShopifySessionIdentity,
  sameShopifySessionIdentity,
  type ShopifySessionIdentity,
} from '@testfeed/shopify-contracts'

/**
 * The latest TestFeed account pass the Worker has handed back, held in module memory only (never localStorage): it is a
 * signed, shop-bound, 10-minute credential, so it must not outlive the tab. Every request carries it so account-only routes
 * skip a fresh Web check; each response that carries a new pass replaces it. Set and read in `shopifyApiFetch` below so every
 * call path — the typed `shopifyApiClient` and direct callers alike — shares one pass.
 */
let latestAccountPass: string | null = null
let latestSessionIdentity: ShopifySessionIdentity | null = null
// Unlike the business request pass, diagnostic snapshots belong to a verified shop/session.
// An old shop's late response must not replace the scope of the page currently being rendered.
const diagnosticScopes = new Map<
  string,
  {
    sequence: number
    scope: ShopifyDiagnosticScope & { sessionIdentity: ShopifySessionIdentity }
  }
>()
let requestSequence = 0
// Request ordering only: a late response for an operation cannot replace its newer answer.
const latestRequests = new Map<string, number>()

export type ShopifyTokenSource = {
  ready?: Promise<unknown>
  idToken?: () => Promise<string>
  sessionIdentity?: ShopifySessionIdentity
}

export class ShopifyBootstrapScopeError extends Error {}

export type ShopifyAppBridge = ShopifyTokenSource & {
  config?: {
    shop?: string
  }
  toast?: {
    show: (message: string, options?: { isError?: boolean }) => void
  }
}

export type ShopifyApiFetchPhase = 'bridge_ready' | 'request'

/** Immutable intake credentials for a view. Late outcomes cannot inherit another view's organization pass. */
export function captureShopifyDiagnosticScope(shopify: ShopifyAppBridge): ShopifyDiagnosticScope {
  const shop = shopify.sessionIdentity?.shopDomain ?? shopify.config?.shop
  const scope = shop ? diagnosticScopes.get(shop)?.scope : undefined
  if (
    !scope ||
    (shopify.sessionIdentity && !sameShopifySessionIdentity(scope.sessionIdentity, shopify.sessionIdentity))
  )
    return { accountPass: null, sessionIdentity: null }
  return scope
}
export type ShopifyDiagnosticScope = { accountPass: string | null; sessionIdentity: ShopifySessionIdentity | null }

type ShopifyApiFetchOptions = {
  init?: RequestInit
  fetchImpl?: typeof fetch
  onPhase?: (phase: ShopifyApiFetchPhase) => void
  diagnosticScope?: ShopifyDiagnosticScope
}

/**
 * Resolve window.fetch after bridge readiness. Supplying Authorization would bypass native renewal.
 */
export async function shopifyApiFetch(
  shopify: ShopifyAppBridge,
  input: RequestInfo | URL,
  options: ShopifyApiFetchOptions = {},
) {
  const signal = options.init?.signal ?? (input instanceof Request ? input.signal : undefined)
  signal?.throwIfAborted()
  const startup = observeShopifyStartupRequest(
    input,
    options.init?.method ?? (input instanceof Request ? input.method : 'GET'),
  )
  let requestScope: string | undefined
  let sequence: number | undefined
  let receivedResponse = false
  try {
    await waitForShopifyOperation(signal, async () => {
      options.onPhase?.('bridge_ready')
      return shopify.ready
    })

    signal?.throwIfAborted()
    const headers = new Headers(options.init?.headers ?? (input instanceof Request ? input.headers : undefined))
    if (headers.has('Authorization')) throw new Error('Shopify API authentication must be supplied by App Bridge.')
    const expectedIdentity = options.diagnosticScope?.sessionIdentity ?? shopify.sessionIdentity
    if (expectedIdentity)
      headers.set(SHOPIFY_EXPECTED_SESSION_IDENTITY_HEADER, encodeURIComponent(JSON.stringify(expectedIdentity)))
    const accountPass = options.diagnosticScope ? options.diagnosticScope.accountPass : latestAccountPass
    if (accountPass) headers.set(SHOPIFY_ACCOUNT_PASS_HEADER, accountPass)

    const shop = shopify.config?.shop
    const requestUrl = input instanceof Request ? input.url : String(input)
    const request = `${options.init?.method ?? (input instanceof Request ? input.method : 'GET')} ${requestUrl}`
    requestScope = JSON.stringify([shop, request])
    sequence = ++requestSequence
    latestRequests.set(requestScope, sequence)
    const fetchImpl = options.fetchImpl ?? window.fetch.bind(window)
    const response = await waitForShopifyOperation(signal, () => {
      options.onPhase?.('request')
      startup?.dispatched()
      return fetchImpl(input, {
        ...options.init,
        headers,
      })
    })
    startup?.responded(response)
    signal?.throwIfAborted()
    // Store any new pass the Worker handed back (a real Web check issues one; a pass-admitted request echoes the same one).
    const receivedPass = response.headers.get(SHOPIFY_ACCOUNT_PASS_HEADER)
    if (response.ok && shopify.sessionIdentity) {
      const identity = readShopifySessionIdentity(response.headers)
      if (!sameShopifySessionIdentity(identity, shopify.sessionIdentity))
        throw new ShopifyBootstrapScopeError('Shopify authenticated session changed during bootstrap.')
    }
    if (!options.diagnosticScope && response.ok) {
      const receivedIdentity = readShopifySessionIdentity(response.headers)
      if (latestSessionIdentity && !sameShopifySessionIdentity(latestSessionIdentity, receivedIdentity))
        latestAccountPass = null
      latestSessionIdentity = receivedIdentity
      if (receivedIdentity.shopDomain === shop) {
        const previous = diagnosticScopes.get(shop)
        if (!previous || previous.sequence < sequence) {
          const sameSession = previous && sameShopifySessionIdentity(previous.scope.sessionIdentity, receivedIdentity)
          diagnosticScopes.set(shop, {
            sequence,
            scope: {
              sessionIdentity: receivedIdentity,
              accountPass: receivedPass ?? (sameSession ? previous.scope.accountPass : null),
            },
          })
        }
      }
    }
    if (!options.diagnosticScope && receivedPass) latestAccountPass = receivedPass
    if (shop && latestRequests.get(requestScope) === sequence) {
      if (response.ok) settleShopifyConnectionRecovery(shop, request, sequence, null)
      else {
        const failure = await readShopifyApiError(response.clone())
        signal?.throwIfAborted()
        if (latestRequests.get(requestScope) === sequence && failure.reconnectUrl) {
          // Existing server-owned action only; never navigate to an arbitrary URL from an error body.
          const expected = `/auth?shop=${encodeURIComponent(shop)}`
          if (failure.reconnectUrl !== expected || !failure.merchantMessage)
            throw new Error('SHOPIFY_CONNECTION_RECOVERY_INVALID: invalid server recovery metadata')
          settleShopifyConnectionRecovery(shop, request, sequence, {
            reconnectUrl: failure.reconnectUrl,
            merchantMessage: failure.merchantMessage,
          })
        }
      }
    }
    receivedResponse = true
    return response
  } finally {
    if (signal?.aborted) startup?.settled('aborted')
    else if (receivedResponse) startup?.settled('response')
    else startup?.settled('rejected')
    if (requestScope && latestRequests.get(requestScope) === sequence) latestRequests.delete(requestScope)
  }
}
